import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

// ─── QUIZ DATA ───────────────────────────────────────────────
const QUIZ = [
  {
    id: 'focus',
    title: "What's your biggest focus right now?",
    subtitle: 'Choose one.',
    type: 'single',
    options: [
      { key: 'business',   label: 'Build a Business',      icon: '🚀' },
      { key: 'fitness',    label: 'Get Fit / Lose Weight',  icon: '💪' },
      { key: 'career',     label: 'Career Growth',          icon: '📈' },
      { key: 'financial',  label: 'Financial Freedom',      icon: '💰' },
      { key: 'spiritual',  label: 'Spiritual Growth',       icon: '🙏' },
      { key: 'creative',   label: 'Creative Pursuit',       icon: '🎨' },
      { key: 'athletic',   label: 'Athletic Performance',   icon: '🏆' },
      { key: 'academic',   label: 'Academic Excellence',    icon: '📚' },
    ],
  },
  {
    id: 'level',
    title: 'How would you describe yourself?',
    subtitle: 'Be honest — this shapes your experience.',
    type: 'single',
    options: [
      { key: 'starting',     label: "I'm just getting started",         icon: '🌱' },
      { key: 'momentum',     label: 'I have some momentum',              icon: '🔄' },
      { key: 'consistent',   label: "I'm consistent but want more",      icon: '📊' },
      { key: 'highperform',  label: "I'm already high-performing",       icon: '⚡' },
    ],
  },
  {
    id: 'challenge',
    title: "What's your biggest challenge?",
    subtitle: 'Pick your main roadblock.',
    type: 'single',
    options: [
      { key: 'consistency',   label: 'Lack of consistency',       icon: '😔' },
      { key: 'goals',         label: 'No clear goals',             icon: '🎯' },
      { key: 'distracted',    label: 'Too distracted',             icon: '📱' },
      { key: 'accountability', label: 'No accountability',         icon: '👤' },
      { key: 'tracking',      label: "Don't track progress",       icon: '📉' },
      { key: 'overwhelmed',   label: 'Doing too much at once',     icon: '🤯' },
    ],
  },
  {
    id: 'tracking',
    title: 'How do you currently track your progress?',
    subtitle: 'Be honest.',
    type: 'single',
    options: [
      { key: 'none',        label: "I don't",              icon: '❌' },
      { key: 'journal',     label: 'Notes or journal',      icon: '📓' },
      { key: 'spreadsheet', label: 'Spreadsheet',           icon: '📊' },
      { key: 'app',         label: 'Another app',           icon: '📲' },
      { key: 'mental',      label: 'In my head',            icon: '🧠' },
    ],
  },
  {
    id: 'areas',
    title: 'Which life areas do you most want to improve?',
    subtitle: 'Pick up to 3.',
    type: 'multi',
    max: 3,
    options: [
      { key: 'health',        label: 'Health',        icon: '💪' },
      { key: 'mind',          label: 'Mind',          icon: '🧠' },
      { key: 'career',        label: 'Career',        icon: '📈' },
      { key: 'relationships', label: 'Relationships', icon: '❤️' },
      { key: 'faith',         label: 'Faith',         icon: '🙏' },
      { key: 'purpose',       label: 'Goals',         icon: '🎯' },
    ],
  },
  {
    id: 'commitment',
    title: 'How serious are you about improving your life right now?',
    subtitle: 'This one matters.',
    type: 'single',
    options: [
      { key: 'browsing',  label: 'Just browsing',     icon: '👀' },
      { key: 'somewhat',  label: 'Somewhat serious',  icon: '🤔' },
      { key: 'serious',   label: 'Very serious',      icon: '💯' },
      { key: 'allin',     label: "I'm all in",        icon: '🔥' },
    ],
  },
]

// ─── ANIMATED SCORE CIRCLE ───────────────────────────────────
function AnimatedScoreCircle({ onComplete }) {
  const [score, setScore] = useState(0)
  const [done, setDone] = useState(false)
  const rafRef = useRef(null)

  useEffect(() => {
    let current = 0
    const duration = 2200
    const start = Date.now()

    function animate() {
      const elapsed = Date.now() - start
      const progress = Math.min(elapsed / duration, 1)
      // Ease out
      const eased = 1 - Math.pow(1 - progress, 3)
      current = Math.round(eased * 100)
      setScore(current)
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        setDone(true)
        setTimeout(onComplete, 800)
      }
    }
    rafRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const circumference = 2 * Math.PI * 85
  const offset = circumference - (circumference * score / 100)
  const scoreColor = score < 50 ? '#e05c5c' : score < 75 ? '#c9a84c' : '#4caf7d'

  return (
    <div style={st.page}>
      <div style={st.readyWrap}>
        <div style={st.readyLogo}>LIFESCORE</div>
        <div style={st.readyTitle}>Are you ready to get your LifeScore?</div>
        <div style={st.readySub}>Let's see what you're capable of.</div>

        <div style={st.circleWrap}>
          <svg width="220" height="220" viewBox="0 0 220 220">
            <defs>
              <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8b6a1a" />
                <stop offset="100%" stopColor="#f0cc7a" />
              </linearGradient>
            </defs>
            <circle cx="110" cy="110" r="85" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
            <circle
              cx="110" cy="110" r="85" fill="none"
              stroke={done ? scoreColor : 'url(#scoreGrad)'}
              strokeWidth="8" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 110 110)"
              style={{ transition: 'stroke 0.3s', filter: `drop-shadow(0 0 12px ${done ? scoreColor : 'rgba(201,168,76,0.6)'})` }}
            />
          </svg>
          <div style={st.circleCenter}>
            <div style={{ ...st.circleScore, color: done ? scoreColor : '#f0cc7a', textShadow: `0 0 20px ${done ? scoreColor : 'rgba(201,168,76,0.5)'}` }}>
              {score}
            </div>
            <div style={st.circleLabel}>YOUR SCORE</div>
          </div>
        </div>

        {done && (
          <div style={st.readyMessage}>
            This is just the beginning. <br />Your real score gets built every day.
          </div>
        )}
      </div>
    </div>
  )
}

// ─── PAYWALL ─────────────────────────────────────────────────
function PaywallScreen({ userId, onComplete }) {
  const [loading, setLoading] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [error, setError] = useState('')
  const [offerings, setOfferings] = useState(null)

  useEffect(() => {
    initRevenueCat()
  }, [])

  async function initRevenueCat() {
    try {
      const { Purchases } = await import(/* webpackIgnore: true */ '@revenuecat/purchases-capacitor')
      await Purchases.configure({ apiKey: 'appl_NUCjSSakHMHYJiqTDoojyHhmdBY' })
      const result = await Purchases.getOfferings()
      setOfferings(result.current)
    } catch (e) {
      console.log('RevenueCat init error:', e)
    }
  }

  async function purchase(productId) {
    setPurchasing(true)
    setError('')
    try {
      const { Purchases } = await import(/* webpackIgnore: true */ '@revenuecat/purchases-capacitor')

      // Find the package matching the product ID
      const pkg = offerings?.availablePackages?.find(
        p => p.product.identifier === productId
      )

      if (!pkg) {
        // Fallback for web/testing — just mark as subscribed
        await markSubscribed()
        return
      }

      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg })

      if (customerInfo.entitlements.active['pro']) {
        await markSubscribed()
      }
    } catch (e) {
      if (e.code !== 'PURCHASE_CANCELLED') {
        setError('Purchase failed. Please try again.')
      }
    }
    setPurchasing(false)
  }

  async function restorePurchases() {
    setLoading(true)
    try {
      const { Purchases } = await import(/* webpackIgnore: true */ '@revenuecat/purchases-capacitor')
      const { customerInfo } = await Purchases.restorePurchases()
      if (customerInfo.entitlements.active['pro']) {
        await markSubscribed()
      } else {
        setError('No previous purchases found.')
      }
    } catch (e) {
      setError('Could not restore purchases.')
    }
    setLoading(false)
  }

  async function markSubscribed() {
    await supabase.from('profiles').update({
      onboarding_complete: true,
      trial_start: new Date().toISOString(),
      is_pro: true,
    }).eq('id', userId)
    onComplete()
  }

  return (
    <div style={st.page}>
      <div style={st.paywallWrap}>
        <div style={st.paywallLogo}>LIFESCORE</div>
        <div style={st.paywallTitle}>Start Your 5-Day Free Trial</div>
        <div style={st.paywallSub}>Full access to everything. Cancel anytime before day 5 and you won't be charged.</div>

        <div style={st.paywallFeatures}>
          {[
            '✓ Daily AI score analysis',
            '✓ Full trends history',
            '✓ AI Coach — Analyze My Day',
            '✓ Weekly Sunday report',
            '✓ Workout Logger',
            '✓ Daily Journal',
            '✓ Add Task with AI scoring',
          ].map(f => <div key={f} style={st.paywallFeat}>{f}</div>)}
        </div>

        {error ? <div style={st.paywallError}>{error}</div> : null}

        <div style={st.planRow}>
          {/* Monthly */}
          <div style={st.planCard} onClick={() => !purchasing && purchase('com.yourapp.lifescore.monthly')}>
            <div style={st.planLabel}>Monthly</div>
            <div style={st.planPrice}>$7.00</div>
            <div style={st.planSub}>/month</div>
            <div style={st.planCta}>{purchasing ? '...' : 'Start Free Trial'}</div>
          </div>

          {/* Yearly */}
          <div style={{ ...st.planCard, ...st.planCardBest }} onClick={() => !purchasing && purchase('com.yourapp.lifescore.yearly')}>
            <div style={st.bestBadge}>BEST VALUE</div>
            <div style={{ ...st.planLabel, color: '#f0cc7a' }}>Yearly</div>
            <div style={{ ...st.planPrice, color: '#f0cc7a' }}>$49.99</div>
            <div style={st.planSub}>save 28%</div>
            <div style={{ ...st.planCta, background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000' }}>
              {purchasing ? '...' : 'Start Free Trial'}
            </div>
          </div>
        </div>

        <div style={st.paywallNote}>5 days free. No charge until trial ends.</div>
        <div style={st.restoreBtn} onClick={restorePurchases}>
          {loading ? 'Restoring...' : 'Restore Purchases'}
        </div>
      </div>
    </div>
  )
}

// ─── MAIN AUTH PAGE ──────────────────────────────────────────
export default function AuthPage() {
  const [step, setStep] = useState('auth') // auth | quiz | ready | paywall
  const [mode, setMode] = useState('login')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [legalModal, setLegalModal] = useState(null) // 'terms' or 'privacy'
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [pendingUserId, setPendingUserId] = useState(null)

  // Quiz state
  const [quizPage, setQuizPage] = useState(0)
  const [answers, setAnswers] = useState({})

  async function handleAppleSignIn() {
    setError('')
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: 'https://lifescore-app.com',
        }
      })
      if (error) setError(error.message)
    } catch (e) {
      setError('Apple sign in failed. Please try again.')
    }
    setLoading(false)
  }

  async function handleSubmit() {
    setError(''); setMessage(''); setLoading(true)
    try {
      if (mode === 'signup') {
        if (!agreedToTerms) { setError('Please agree to the Terms of Service and Privacy Policy to continue.'); setLoading(false); return }
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: fullName } }
        })
        if (error) { setError(error.message); setLoading(false); return }
        if (data?.user) {
          setPendingUserId(data.user.id)
          setStep('quiz')
        } else {
          setMessage('Check your email to confirm your account, then log in.')
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) { setError(error.message); setLoading(false); return }
        // Check if returning user completed onboarding
        const { data: profile } = await supabase.from('profiles').select('onboarding_complete').eq('id', data.user.id).single()
        if (!profile?.onboarding_complete) {
          setPendingUserId(data.user.id)
          setStep('quiz')
        } else {
          window.location.href = '/'
        }
      }
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  function handleQuizAnswer(questionId, value, type, max) {
    if (type === 'multi') {
      const current = answers[questionId] || []
      if (current.includes(value)) {
        setAnswers(prev => ({ ...prev, [questionId]: current.filter(v => v !== value) }))
      } else if (current.length < max) {
        setAnswers(prev => ({ ...prev, [questionId]: [...current, value] }))
      }
    } else {
      setAnswers(prev => ({ ...prev, [questionId]: value }))
      // Auto advance for single select
      setTimeout(() => {
        if (quizPage < QUIZ.length - 1) setQuizPage(p => p + 1)
        else goToReady()
      }, 300)
    }
  }

  async function goToReady() {
    // Save quiz answers and goals to profile
    const areas = answers.areas || []
    if (pendingUserId) {
      await supabase.from('profiles').update({
        goals: areas,
        quiz_answers: answers,
      }).eq('id', pendingUserId)
    }
    setStep('ready')
  }

  function handleMultiNext() {
    if (quizPage < QUIZ.length - 1) setQuizPage(p => p + 1)
    else goToReady()
  }

  // ── QUIZ STEP ──
  if (step === 'quiz') {
    const q = QUIZ[quizPage]
    const currentAnswer = answers[q.id]
    const isMulti = q.type === 'multi'
    const hasAnswer = isMulti ? (currentAnswer?.length > 0) : !!currentAnswer
    const progress = ((quizPage + 1) / QUIZ.length) * 100

    return (
      <div style={st.page}>
        <div style={st.quizWrap}>
          {/* Progress bar */}
          <div style={st.progressBar}>
            <div style={{ ...st.progressFill, width: `${progress}%` }} />
          </div>

          <div style={st.quizContent}>
            <div style={st.quizStep}>{quizPage + 1} of {QUIZ.length}</div>
            <div style={st.quizTitle}>{q.title}</div>
            <div style={st.quizSub}>{q.subtitle}</div>

            <div style={isMulti ? st.optionsGrid : st.optionsGrid}>
              {q.options.map(opt => {
                const selected = isMulti
                  ? (currentAnswer || []).includes(opt.key)
                  : currentAnswer === opt.key
                const disabled = isMulti && !selected && (currentAnswer?.length || 0) >= q.max

                return (
                  <div
                    key={opt.key}
                    style={{
                      ...st.optionCard,
                      background: selected ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)',
                      border: selected ? '1px solid rgba(201,168,76,0.5)' : '1px solid rgba(255,255,255,0.12)',
                      opacity: disabled ? 0.4 : 1,
                      cursor: disabled ? 'default' : 'pointer',
                      transform: selected ? 'scale(1.02)' : 'scale(1)',
                    }}
                    onClick={() => !disabled && handleQuizAnswer(q.id, opt.key, q.type, q.max)}
                  >
                    <span style={st.optionIcon}>{opt.icon}</span>
                    <span style={st.optionLabel}>{opt.label}</span>
                    {selected && <div style={st.optionCheck}>✓</div>}
                  </div>
                )
              })}
            </div>

            {isMulti && (
              <div
                style={{ ...st.nextBtn, opacity: hasAnswer ? 1 : 0.4 }}
                onClick={hasAnswer ? handleMultiNext : undefined}
              >
                {quizPage === QUIZ.length - 1 ? "See My Score →" : "Next →"}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── READY STEP ──
  if (step === 'ready') {
    return <AnimatedScoreCircle onComplete={() => setStep('paywall')} />
  }

  // ── PAYWALL STEP ──
  if (step === 'paywall') {
    return (
      <PaywallScreen
        userId={pendingUserId}
        onComplete={() => { window.location.href = '/' }}
      />
    )
  }

  // ── AUTH STEP ──
  return (
    <div style={st.page}>
      <div style={st.card}>
        <div style={st.logo}>LIFESCORE</div>
        <div style={st.tagline}>
          {mode === 'login' ? 'Welcome back.' : 'Measure your life. Improve it daily.'}
        </div>
        <div style={st.form}>
          {mode === 'signup' && (
            <input style={st.input} placeholder="Full name" value={fullName}
              onChange={e => setFullName(e.target.value)} />
          )}
          <input style={st.input} type="email" placeholder="Email address" value={email}
            onChange={e => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" />
          <input style={st.input} type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} />
          {mode === 'signup' && (
            <div style={st.agreeRow} onClick={() => setAgreedToTerms(v => !v)}>
              <div style={{ ...st.checkbox, background: agreedToTerms ? '#c9a84c' : 'transparent', borderColor: agreedToTerms ? '#c9a84c' : 'rgba(255,255,255,0.2)' }}>
                {agreedToTerms && <span style={st.checkmark}>✓</span>}
              </div>
              <div style={st.agreeText}>
                I agree to the{' '}
                <span style={st.legalLink} onClick={e => { e.stopPropagation(); setLegalModal('terms') }}>Terms of Service</span>
                {' '}and{' '}
                <span style={st.legalLink} onClick={e => { e.stopPropagation(); setLegalModal('privacy') }}>Privacy Policy</span>
              </div>
            </div>
          )}
          {error && <div style={st.error}>{error}</div>}
          {message && <div style={st.success}>{message}</div>}
          <div style={{ ...st.btn, opacity: loading ? 0.6 : 1 }} onClick={handleSubmit}>
            {loading ? 'Loading...' : mode === 'login' ? 'Sign In' : 'Create Account →'}
          </div>

          <div style={st.dividerRow}>
            <div style={st.dividerLine} />
            <div style={st.dividerText}>or</div>
            <div style={st.dividerLine} />
          </div>

          <div style={st.appleBtn} onClick={handleAppleSignIn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Sign in with Apple
          </div>
        </div>
        <div style={st.toggle}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <span style={st.toggleLink}
            onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setMessage('') }}>
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </span>
        </div>
      </div>

      {/* LEGAL MODAL */}
      {legalModal && (
        <>
          <div style={st.legalOverlay} onClick={() => setLegalModal(null)} />
          <div style={st.legalModal}>
            <div style={st.legalHeader}>
              <div style={st.legalTitle}>{legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}</div>
              <div style={st.legalClose} onClick={() => setLegalModal(null)}>✕</div>
            </div>
            <div style={st.legalBody}>
              {legalModal === 'terms' ? (
                <>
                  <p style={st.legalP}><strong>Last updated: April 2026</strong></p>
                  <p style={st.legalP}>Welcome to LifeScore. By creating an account, you agree to these Terms of Service. Please read them carefully.</p>
                  <p style={st.legalH}>1. Use of the App</p>
                  <p style={st.legalP}>LifeScore is a personal growth and habit tracking application. You must be at least 13 years old to use this app. You are responsible for maintaining the security of your account and password.</p>
                  <p style={st.legalH}>2. Subscription & Billing</p>
                  <p style={st.legalP}>LifeScore offers a 5-day free trial followed by a paid subscription ($7.00/month or $49.99/year). Payment is charged through your Apple ID. Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period. You can manage or cancel your subscription in your Apple ID account settings.</p>
                  <p style={st.legalH}>3. Your Content</p>
                  <p style={st.legalP}>You own the content you create in LifeScore (journal entries, tasks, goals). We do not claim ownership of your data. You grant us a limited license to store and process your content solely to provide the service.</p>
                  <p style={st.legalH}>4. Prohibited Use</p>
                  <p style={st.legalP}>You may not use LifeScore for any unlawful purpose, to harass others, or to submit false or misleading information. We reserve the right to suspend accounts that violate these terms.</p>
                  <p style={st.legalH}>5. Disclaimer</p>
                  <p style={st.legalP}>LifeScore is not a medical, mental health, or fitness professional service. Content and AI coaching are for informational purposes only and should not replace professional advice.</p>
                  <p style={st.legalH}>6. Changes to Terms</p>
                  <p style={st.legalP}>We may update these terms from time to time. Continued use of the app after changes constitutes acceptance of the new terms.</p>
                  <p style={st.legalH}>7. Contact</p>
                  <p style={st.legalP}>For questions about these terms, contact us at support@lifescore-app.com</p>
                </>
              ) : (
                <>
                  <p style={st.legalP}><strong>Last updated: April 2026</strong></p>
                  <p style={st.legalP}>Your privacy is important to us. This policy explains what data LifeScore collects, how we use it, and your rights.</p>
                  <p style={st.legalH}>1. Data We Collect</p>
                  <p style={st.legalP}>We collect information you provide directly: your name, email address, and the content you log (tasks, journal entries, habits, scores). We also collect basic usage data to improve the app.</p>
                  <p style={st.legalH}>2. How We Use Your Data</p>
                  <p style={st.legalP}>Your data is used solely to provide and improve the LifeScore service — including generating your scores, AI coaching insights, and journal statistics. We do not sell your personal data to third parties.</p>
                  <p style={st.legalH}>3. AI Processing</p>
                  <p style={st.legalP}>Task descriptions you enter are sent to Anthropic's Claude API to evaluate and categorize them. This processing is used only to generate your points and coaching insights. We do not store your data with Anthropic beyond what is required for the API call.</p>
                  <p style={st.legalH}>4. Data Storage</p>
                  <p style={st.legalP}>Your data is stored securely using Supabase, a third-party database provider. Profile photos are stored in Supabase Storage. All data is encrypted in transit and at rest.</p>
                  <p style={st.legalH}>5. Apple Health</p>
                  <p style={st.legalP}>If you choose to connect Apple Health, we access health metrics (steps, sleep, heart rate, calories) only to display them in the app and generate your health score. This data is never shared with third parties.</p>
                  <p style={st.legalH}>6. Your Rights</p>
                  <p style={st.legalP}>You can delete your account and all associated data at any time by contacting us at support@lifescore-app.com. You can also request a copy of your data at any time.</p>
                  <p style={st.legalH}>7. Children's Privacy</p>
                  <p style={st.legalP}>LifeScore is not directed at children under 13. We do not knowingly collect personal information from children under 13.</p>
                  <p style={st.legalH}>8. Contact</p>
                  <p style={st.legalP}>For privacy questions, contact us at support@lifescore-app.com</p>
                </>
              )}
            </div>
            <div style={st.legalAgreeBtn} onClick={() => { setAgreedToTerms(true); setLegalModal(null) }}>
              I Agree ✓
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const st = {
  appleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '14px', background: '#000', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  dividerRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  dividerLine: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' },
  dividerText: { fontSize: '12px', color: '#6b6860' },
  agreeRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', padding: '4px 0' },
  checkbox: { width: '20px', height: '20px', borderRadius: '5px', border: '1.5px solid', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px', transition: 'all 0.15s' },
  checkmark: { fontSize: '12px', color: '#000', fontWeight: '700' },
  agreeText: { fontSize: '13px', color: '#6b6860', lineHeight: '1.5' },
  legalLink: { color: '#c9a84c', textDecoration: 'underline', cursor: 'pointer' },
  // Legal modal
  legalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 400 },
  legalModal: { position: 'fixed', bottom: 0, left: 0, right: 0, top: '8%', background: '#1a1a26', borderRadius: '20px 20px 0 0', border: '1px solid rgba(255,255,255,0.12)', zIndex: 401, display: 'flex', flexDirection: 'column' },
  legalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 },
  legalTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px', color: '#e8e6df' },
  legalClose: { fontSize: '18px', color: '#6b6860', cursor: 'pointer', padding: '4px' },
  legalBody: { flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '4px' },
  legalH: { fontSize: '13px', fontWeight: '700', color: '#c9a84c', marginTop: '12px' },
  legalP: { fontSize: '13px', color: '#a09e97', lineHeight: '1.6' },
  legalAgreeBtn: { margin: '16px 24px 32px', padding: '15px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700', flexShrink: 0 },
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' },

  // Auth
  card: { width: '100%', maxWidth: '380px', background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '36px 28px', position: 'relative', zIndex: 1 },
  logo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '4px', color: '#c9a84c', marginBottom: '8px', textAlign: 'center' },
  tagline: { fontFamily: "'DM Serif Display', serif", fontStyle: 'italic', fontSize: '16px', color: 'rgba(232,230,223,0.5)', textAlign: 'center', marginBottom: '28px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  input: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '10px', padding: '13px 16px', color: '#e8e6df', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", outline: 'none', WebkitAppearance: 'none' },
  btn: { marginTop: '4px', padding: '16px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontFamily: "'DM Sans', sans-serif", fontWeight: '700', fontSize: '15px', letterSpacing: '0.5px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center' },
  error: { background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '8px', padding: '10px 14px', color: '#e05c5c', fontSize: '13px' },
  success: { background: 'rgba(76,175,125,0.1)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '8px', padding: '10px 14px', color: '#4caf7d', fontSize: '13px' },
  toggle: { textAlign: 'center', marginTop: '20px', fontSize: '13px', color: 'rgba(232,230,223,0.4)' },
  toggleLink: { color: '#c9a84c', cursor: 'pointer' },

  // Quiz
  quizWrap: { width: '100%', maxWidth: '420px', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  progressBar: { height: '3px', background: 'rgba(255,255,255,0.08)', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #8b6a1a, #f0cc7a)', transition: 'width 0.4s ease', borderRadius: '0 2px 2px 0' },
  quizContent: { padding: '60px 24px 40px', flex: 1, display: 'flex', flexDirection: 'column' },
  quizStep: { fontSize: '11px', letterSpacing: '2px', color: '#6b6860', marginBottom: '12px', textTransform: 'uppercase' },
  quizTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: '#e8e6df', lineHeight: '1.3', marginBottom: '8px' },
  quizSub: { fontSize: '14px', color: '#6b6860', marginBottom: '28px' },
  optionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '24px' },
  optionCard: { borderRadius: '14px', padding: '16px 14px', cursor: 'pointer', position: 'relative', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '6px' },
  optionIcon: { fontSize: '24px' },
  optionLabel: { fontSize: '13px', fontWeight: '600', color: '#e8e6df', lineHeight: '1.3' },
  optionCheck: { position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: '#c9a84c', color: '#000', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  nextBtn: { padding: '16px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700' },

  // Ready / animated circle
  readyWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 24px 40px', width: '100%', maxWidth: '380px' },
  readyLogo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '4px', color: '#c9a84c', marginBottom: '24px' },
  readyTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '26px', color: '#e8e6df', lineHeight: '1.3', marginBottom: '8px' },
  readySub: { fontSize: '14px', color: '#6b6860', marginBottom: '36px' },
  circleWrap: { position: 'relative', width: '220px', height: '220px', marginBottom: '28px' },
  circleCenter: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  circleScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '80px', lineHeight: 1, transition: 'color 0.3s, text-shadow 0.3s' },
  circleLabel: { fontSize: '10px', letterSpacing: '2px', color: '#6b6860', marginTop: '4px' },
  readyMessage: { fontSize: '15px', color: '#6b6860', lineHeight: '1.7', maxWidth: '280px' },

  // Paywall
  paywallWrap: { width: '100%', maxWidth: '400px', padding: '40px 24px' },
  paywallLogo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '4px', color: '#c9a84c', textAlign: 'center', marginBottom: '20px' },
  paywallTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '26px', color: '#e8e6df', textAlign: 'center', marginBottom: '10px' },
  paywallSub: { fontSize: '13px', color: '#6b6860', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' },
  paywallFeatures: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px 20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' },
  paywallFeat: { fontSize: '13px', color: '#e8e6df' },
  planRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  planCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px 16px', textAlign: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden' },
  planCardBest: { background: 'linear-gradient(145deg, rgba(201,168,76,0.15), #1a1a26)', border: '1px solid rgba(201,168,76,0.4)' },
  bestBadge: { fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', color: '#c9a84c', background: 'rgba(201,168,76,0.15)', padding: '3px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '8px' },
  planLabel: { fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  planPrice: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: '#e8e6df', lineHeight: 1, marginBottom: '4px' },
  planSub: { fontSize: '11px', color: '#6b6860', marginBottom: '14px' },
  planCta: { padding: '10px', background: 'rgba(255,255,255,0.08)', color: '#e8e6df', borderRadius: '8px', fontSize: '12px', fontWeight: '700' },
  paywallNote: { textAlign: 'center', fontSize: '12px', color: '#6b6860', marginBottom: '12px' },
  paywallError: { background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '8px', padding: '10px 14px', color: '#e05c5c', fontSize: '13px', marginBottom: '12px', textAlign: 'center' },
  restoreBtn: { textAlign: 'center', fontSize: '12px', color: '#6b6860', cursor: 'pointer', padding: '8px', textDecoration: 'underline' },
}
